import React from "react";
import ReactDOM from "react-dom";
import { RelayEnvironmentProvider } from "react-relay";

import { isEqual } from "lodash";
import { Node as ProseNode } from "prosemirror-model";
import { Decoration, EditorView, type NodeView } from "prosemirror-view";

import { environment } from "~/models";

/**
 * For nodes, a function that can be called to get the node's current position – that is, the position immediately
 * **before** the opening token of the node. For marks, it's a boolean that indicates whether the mark's content is
 * inline.
 */
export type getPosHandler = boolean | (() => number);
export type ReactNodeViewProps = {
	node: ProseNode;
	options: ReactNodeView["options"];
	view: EditorView;
	contentDOM: HTMLElement;
	dom: HTMLElement;
	setRef: (el: HTMLDivElement) => void;
	getPos: getPosHandler;
	decorations: readonly Decoration[];
};

export abstract class ReactNodeView implements NodeView {
	dom: HTMLElement;
	contentDOM: HTMLElement;
	getPos: getPosHandler;
	node: ProseNode;
	view: EditorView;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	options: { [key: string]: any };
	decorations: readonly Decoration[];

	contentWrapper = document.createElement("div");
	isBlock: boolean;

	abstract getComponent(): React.ComponentType<ReactNodeViewProps>;

	constructor(
		node: ProseNode,
		view: EditorView,
		getPos: getPosHandler,
		decorations: readonly Decoration[],
		options = {},
		isBlock = false,
	) {
		this.node = node;
		this.view = view;
		this.getPos = getPos;
		this.onMount = this.onMount.bind(this);
		this.isBlock = isBlock;

		this.dom = this.getContainer();
		this.contentDOM = this.getContentContainer();
		this.options = options;
		this.decorations = decorations;
		this.renderComponent(this.onMount);

		// Sometimes React renders synchronously, sometimes it doesn't. ¯\_(ツ)_/¯
		// If it didn't, we need contentDOM to be in the DOM, because Prose will assume it's there,
		// and can crash if it isn't.
		if (!this.contentDOM.parentNode) {
			if (this.isBlock) {
				this.contentWrapper.appendChild(this.contentDOM);
			} else {
				this.dom.appendChild(this.contentDOM);
			}
		}
	}

	renderComponent(onRender?: () => void) {
		const Comp = this.getComponent();
		const mountPoint = this.isBlock ? this.contentWrapper : this.dom;
		ReactDOM.render(
			<RelayEnvironmentProvider environment={environment}>
				<Comp
					dom={this.dom}
					node={this.node}
					options={this.options}
					view={this.view}
					setRef={this.setContentContainer}
					contentDOM={this.contentDOM}
					decorations={this.decorations}
					getPos={this.getPos}
					key={this.node.attrs.blockId}
				/>
			</RelayEnvironmentProvider>,
			mountPoint,
			onRender,
		);
	}

	update(node: ProseNode, decorations: readonly Decoration[]): boolean {
		// If we're given a different block or the block changes type, we return
		// false which should have ProseMirror recreate the nodeview (in all
		// likelihood this nodeview is not the correct one for the new node type).
		if (
			node.attrs.blockId !== this.node.attrs.blockId ||
			node.attrs.creator !== this.node.attrs.creator ||
			node.type !== this.node.type
		) {
			return false;
		}
		// The decorations array is always different (even if the decorations are
		// the same) so we'll only update it when they're not equivalent.
		if (!isEqual(this.decorations, decorations)) {
			this.decorations = Array.from(decorations);
		}
		this.node = node; // The new node is a different object (causes re-render)
		this.renderComponent();
		return true;
	}

	getContainer() {
		const dom = document.createElement("div");
		if (this.isBlock) {
			this.contentWrapper.setAttribute("pmcontentWrapper", "");
			dom.setAttribute("pmblock", "");
			dom.appendChild(this.contentWrapper);
		}
		return dom;
	}

	getContentContainer() {
		const el = document.createElement("div");
		el.classList.add("react-node-view-content");
		return el;
	}

	onMount(): void {}

	setContentContainer = (element: HTMLDivElement) => {
		this.dom.classList.add(`react-node-view`, `react-node-view-${this.node.type.name}`);
		if (this.node.attrs.originalBlockId) {
			this.dom.setAttribute("data-copied", "true");
		}
		if (this.contentDOM) {
			// An empty contentDOM tag will cause issue with chrome, add any attr to it would work
			this.contentDOM.setAttribute("ReactNodeViewContent", "");
			//this.contentDOM.setAttribute("pmcontent", "");
		}

		// if (element && element.parentNode && this.contentDOM && element !== this.contentDOM) {
		// 	console.log("replacing");
		// 	element.parentNode.replaceChild(this.contentDOM, element);
		// }

		// for some reason appending seems to be faster than replacing (see commented out code above)
		if (element) {
			element.append(this.contentDOM);
		}
	};

	get attrs() {
		return this.node.attrs;
	}

	destroy() {
		const mountNode = this.isBlock ? this.contentWrapper : this.dom;
		setTimeout(() => {
			// Give it some time to run animations
			ReactDOM.unmountComponentAtNode(mountNode);
		}, 500);
	}

	setAttr = (key: string, value: unknown) => {
		this.view.dispatch(
			this.view.state.tr.setNodeMarkup((this.getPos as () => number)(), this.node.type, {
				...this.node.attrs,
				[key]: value,
			}),
		);
	};
}

export default ReactNodeView;
